import React, { useCallback, useEffect, useMemo, useRef,useState } from 'react';

import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { FormattedMessage, injectIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { connect, useDispatch } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import {
  AppWrapper,
  Block,
  Coupon,
  Feedback,
} from 'components';
import { AuthProtected } from 'containers';
import { actions as couponActions } from 'coupon-common/src/modules/coupons';
import { actions as locationActions } from 'coupon-common/src/modules/locations';
import { useNextPageHandler } from 'hooks';

import messages from './messages';
import styles from './WebsiteCoupons.module.scss';

const DEFAULT_TRANSITION_TIMEOUT = 300;
const DEFAULT_NEXT_PAGE_LOADING_SKELETON_HEIGHT = 50;
const DEFAULT_NEXT_PAGE_LOADING_SKELETON_DURATION = 0.9;
const MAX_CITY_RESULTS = 8;

export const WebsiteCoupons = ({
  intl,
  coupons,
  businessInfo,
  currentCityId,
  currentCity,
  allCities,
  viewCounts,
}) => {
  const dispatch = useDispatch();
  const citySearchRef = useRef(null);
  const lastFetchedCityIdRef = useRef(null);

  const [selectedCityId, setSelectedCityId] = useState(currentCityId || null);
  const [citySearch, setCitySearch] = useState('');
  const [showCityResults, setShowCityResults] = useState(false);

  const websiteCityCouponsState =
    (coupons &&
      coupons.result &&
      coupons.result.websiteCityCoupons) || {};

  const couponEntities =
    (coupons &&
      coupons.entities &&
      coupons.entities.coupons) || {};

  const couponsList = useMemo(function() {
    return (websiteCityCouponsState.results || [])
      .map(function(id) {
        return couponEntities[id];
      })
      .filter(Boolean);
  }, [websiteCityCouponsState.results, couponEntities]);

  const fetchCouponAnalytics = useCallback(function() {
    if (!couponsList.length) {
      return;
    }
  
    const couponIds = couponsList.map(function(coupon) {
      return coupon.id;
    });
  
    dispatch(couponActions.getViewCounts({ coupon_ids: couponIds }));
  }, [dispatch, couponsList]);

  const selectedCity = useMemo(function() {
    if (!selectedCityId) {
      return currentCity;
    }

    return (allCities || []).find(function(city) {
      return city.id === Number(selectedCityId);
    }) || currentCity;
  }, [allCities, selectedCityId, currentCity]);

  const filteredCities = useMemo(function() {
    const normalizedSearch = (citySearch || '').trim().toLowerCase();

    if (!normalizedSearch) {
      return (allCities || []).slice(0, MAX_CITY_RESULTS);
    }

    return (allCities || [])
      .filter(function(city) {
        return city.name &&
          city.name.toLowerCase().indexOf(normalizedSearch) !== -1;
      })
      .slice(0, MAX_CITY_RESULTS);
  }, [allCities, citySearch]);

  const isCouponsFetching = websiteCityCouponsState.fetching;
  const couponsFetchingError = websiteCityCouponsState.error;
  const couponsNextPageExist = !!websiteCityCouponsState.next;
  const lastCouponsListItem = couponsList.slice(-1)[0];

  const currentCityName =
    (selectedCity && selectedCity.name) ||
    intl.formatMessage(messages.defaultCityName);

  useEffect(function() {
    dispatch(locationActions.getCities());
  }, [dispatch]);

  useEffect(function() {
    if (currentCityId && !selectedCityId) {
      setSelectedCityId(currentCityId);
    }
  }, [currentCityId, selectedCityId]);

  useEffect(function() {
    if (selectedCity && selectedCity.name) {
      setCitySearch(selectedCity.name);
    }
  }, [selectedCity]);

  useEffect(function() {
    if (selectedCityId) {
      dispatch(couponActions.getWebsiteCityCoupons({ city: Number(selectedCityId) }));
    }
  }, [dispatch, selectedCityId]);

  useEffect(function() {
    if (!selectedCityId) {
      return;
    }
  
    if (!couponsList.length) {
      return;
    }
  
    fetchCouponAnalytics();
    lastFetchedCityIdRef.current = selectedCityId;
  }, [selectedCityId, couponsList, fetchCouponAnalytics]);

  useEffect(function() {
    const handleOutsideClick = function(event) {
      if (
        citySearchRef.current &&
        !citySearchRef.current.contains(event.target)
      ) {
        setShowCityResults(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return function() {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useNextPageHandler(
    couponActions.getWebsiteCityCouponsNextPage,
    couponsNextPageExist,
    isCouponsFetching,
    { city: Number(selectedCityId) }
  );

  const handleCitySelect = function(city) {
    setSelectedCityId(city.id);
    setCitySearch(city.name);
    setShowCityResults(false);
  };

  const renderAnalyticsData = useCallback(function(data, labelMessage) {
    return (
      <div styleName="analytics-pill">
        <span styleName="analytics-value">{data || 0}</span>
        <span styleName="analytics-label">
          <FormattedMessage {...labelMessage} />
        </span>
      </div>
    );
  }, []);

  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText={intl.formatMessage(messages.pageHeadingText, {
        cityName: currentCityName,
      })}
    >
      <AuthProtected />
      <div className="col-md-10">
        <Block>
          <div styleName="top-row">
            <FormattedMessage
              tagName="h5"
              {...messages.couponPreviewLabel}
              values={{
                cityName: currentCityName,
              }}
            />

            <div styleName="city-selector-wrap" ref={citySearchRef}>
              <label styleName="city-label">
                <FormattedMessage {...messages.changeCityLabel} />
              </label>

              <div styleName="city-search-box">
                <input
                  type="text"
                  value={citySearch}
                  onChange={function(e) {
                    setCitySearch(e.target.value);
                    setShowCityResults(true);
                  }}
                  onFocus={function() {
                    setShowCityResults(true);
                  }}
                  placeholder={intl.formatMessage(messages.citySearchPlaceholder)}
                  styleName="city-search-input"
                />

                {showCityResults && filteredCities.length > 0 && (
                  <div styleName="city-results-dropdown">
                    {filteredCities.map(function(city) {
                      const isActive = Number(selectedCityId) === city.id;

                      return (
                        <button
                          type="button"
                          key={city.id}
                          styleName={isActive ? 'city-result-item-active' : 'city-result-item'}
                          onClick={function() {
                            handleCitySelect(city);
                          }}
                        >
                          {city.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {showCityResults && filteredCities.length === 0 && (
                  <div styleName="city-results-dropdown">
                    <div styleName="city-no-results">
                      <FormattedMessage {...messages.noCityResultsText} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isCouponsFetching &&
            couponsList.length === 0 &&
            !couponsFetchingError && (
              <div styleName="empty-state">
                <FormattedMessage
                  {...messages.emptyStateText}
                  values={{
                    cityName: currentCityName,
                  }}
                />
              </div>
            )}

          <TransitionGroup>
            {couponsList.map(function(item) {
              const isLastCouponItem =
                lastCouponsListItem && lastCouponsListItem.id === item.id;

              return (
                <CSSTransition
                  key={item.id}
                  timeout={DEFAULT_TRANSITION_TIMEOUT}
                  classNames="coupon-list-animation"
                >
                  <div>
                    <div className="mb-3">
                      <span className="text-muted">
                        <FormattedMessage {...messages.categoryLabel} />
                      </span>
                      <span className="ml-1">
                        {(item.category && item.category.name) || '-'}
                      </span>
                    </div>

                    <div className="d-flex flex-wrap">
  <div styleName="coupon-container">
    <Coupon data={item} businessData={businessInfo} />
  </div>

  <div styleName="coupon-analytics-row">
    {renderAnalyticsData(viewCounts[item.id] || 0, messages.viewsText)}
    {renderAnalyticsData(item.save_coupon_count, messages.likesText)}
  </div>
</div>

                    {!isLastCouponItem && (
                      <hr className="coupons-list-item-separator" />
                    )}
                  </div>
                </CSSTransition>
              );
            })}
          </TransitionGroup>

          {isCouponsFetching && (
            <Skeleton
              className="mt-5"
              height={DEFAULT_NEXT_PAGE_LOADING_SKELETON_HEIGHT}
              duration={DEFAULT_NEXT_PAGE_LOADING_SKELETON_DURATION}
            />
          )}

          <Feedback
            show={!!couponsFetchingError}
            invalid
            content={couponsFetchingError}
          />
        </Block>
      </div>
    </AppWrapper>
  );
};

WebsiteCoupons.propTypes = {
  intl: PropTypes.object,
  coupons: PropTypes.object,
  businessInfo: PropTypes.object,
  currentCityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentCity: PropTypes.object,
  allCities: PropTypes.array,
  viewCounts: PropTypes.object,
};

const mapStateToProps = function(state) {
  const businessInfo = state.businessInfo && state.businessInfo.data;
  const rawCity = businessInfo && businessInfo.city;

  let currentCityId = null;
  let currentCity = null;

  if (rawCity && typeof rawCity === 'object') {
    currentCityId = rawCity.id;
    currentCity = rawCity;
  } else {
    currentCityId = rawCity;
    currentCity =
      state.locations &&
      state.locations.entities &&
      state.locations.entities.cities &&
      state.locations.entities.cities[rawCity];
  }

  const cityEntities =
    state.locations &&
    state.locations.entities &&
    state.locations.entities.cities;

  const allCities = cityEntities ? Object.values(cityEntities) : [];

  return {
    coupons: state.coupons,
    businessInfo: businessInfo,
    currentCityId: currentCityId,
    currentCity: currentCity,
    allCities: allCities,
    viewCounts: state.coupons.viewCounts || {},
  };
};

export default connect(mapStateToProps)(
  injectIntl(CSSModule(WebsiteCoupons, styles, { allowMultiple: true })),
);